<template>
  <auth-layout class="authForm">
    <template #title>
      <div class="authForm_title">{{ $t('auth.form.titleEmail') }}</div>
    </template>
    <template #description>
      <div class="authForm_description">{{ $t('auth.form.descriptionEamil') }}</div>
    </template>
    <template #content>
      <div class="authForm_content">
        <form class="authForm_form" @submit.prevent="handleContinue">
          <div class="authForm_input">
            <InputV3
              v-model="email"
              :placeholder="$t('auth.email')"
              label="Email"
              type="email"
              name="email"
              required
            />
            <div class="authForm_input_error" v-if="error">{{ error }}</div>
          </div>
          <div>
            <ac-button
              class="authForm_button-continue"
              type="submit"
              variant="tonal"
              block
              :disabled="!email"
            >
              {{ isLoading ? $t('auth.form.buttonSend') : $t('continue') }}
              <div v-if="isLoading" class="authForm_preloader">
                <ac-preloader />
              </div>
            </ac-button>
          </div>
        </form>

        <auth-enter-with :error="queryError" />

        <ac-button
          variant="text"
          color="gray"
          class="authForm_enter-phone"
          block
          @click="handleEnterPhone"
        >
          {{ $t('auth.enterByPhone') }}
        </ac-button>
      </div>
    </template>
  </auth-layout>
</template>

<script lang="ts" setup>
import AuthLayout from '@web/platform.v3/layouts/auth.vue'
import InputV3 from '@web/platform.v3/components/input.vue'
import AcButton from '@ui-kit/components/AcButton.vue'
import AcPreloader from '@ui-kit/components/AcPreloader.vue'
import AuthEnterWith from '@web/platform.v3/components/auth/enter-with.vue'

import { computed, ref, watch } from 'vue'
import { useRouter } from 'vue-router'
import { useStore } from '@web/store'
import { useAuthCatchErrors } from '@web/platform.v3/composables/useAuthCatchErrors'

defineOptions({
  name: 'AuthEmailForm'
})

const router = useRouter()
const store = useStore()
const email = ref('')
const isLoading = ref(false)
const error = ref<string>('')

const storedEmail = computed(() => store.getters['authV3/email'])
const isTimerActive = computed(() => store.getters['authV3/isTimerActive'])

const { catchError, queryError } = useAuthCatchErrors()

const handleContinue = async () => {
  if (isLoading.value) return
  isLoading.value = true
  error.value = ''
  queryError.value = ''
  if (storedEmail.value === email.value && isTimerActive.value) {
    store.commit('authV3/setNeedStartTimer', false)
    store.commit('authV3/setEmailStatus', 'code')
    return
  }

  try {
    await store.dispatch('authV3/requestEmailCode', email.value)
    store.commit('authV3/setNeedStartTimer', true)
    store.commit('authV3/setEmail', email.value)
    store.commit('authV3/setEmailStatus', 'code')
  } catch (e) {
    error.value = catchError(e)
  } finally {
    isLoading.value = false
  }
}

function handleEnterPhone () {
  store.commit('authV3/reset')
  router.replace({ name: 'login', params: { type: 'phone' } })
}

watch(storedEmail, () => {
  if (storedEmail.value) {
    email.value = storedEmail.value
  }
}, {
  immediate: true,
  once: true
})
</script>

<style lang="scss" scoped>
  @import '@web/platform.v3/styles/auth/form.scss';
</style>
